'use client';

import { useAppBridge, WSContext } from '@/libs/embed-brige/embed-bridge';
import { useGlobalStore } from '@/store/global';
import type { EmbedParams } from '@/store/global/initial-state';
import { useUserStore } from '@/store/user';
import { authSelectors } from '@/store/user/slices/auth/selectors';
import { useWorkspaceStore } from '@/store/workspace';
import { LocaleMode } from '@/types_cp/locale';
import { parseEmbedParams } from '@/utils/embed-app-url';
import { useRouter, useSearchParams } from 'next/navigation';
import { FC, PropsWithChildren, useEffect, useMemo, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';

const EMBED_SESSION_STORAGE_KEY = 'copilot-embed-data';

const readEmbedStateFromSessionStorage = (): EmbedParams | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const storedValue = window.sessionStorage.getItem(EMBED_SESSION_STORAGE_KEY);
    if (!storedValue) {
      return null;
    }
    const embedData: EmbedParams = JSON.parse(storedValue);
    if (embedData.debug) {
      console.log('[AppEmbedBridge] Read embed state from sessionStorage:', embedData);
    }
    return embedData;
  } catch {
    return null;
  }
};

const saveEmbedStateToSessionStorage = (value: EmbedParams): void => {
  if (typeof window === 'undefined') {
    return;
  }
  if (value.debug) {
    console.log('[AppEmbedBridge] Saving embed state to sessionStorage:', value);
  }
  try {
    window.sessionStorage.setItem(EMBED_SESSION_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Ignore storage failures so bridge setup can continue.
  }
};

interface AppEmbedBridgeProps extends PropsWithChildren {}

const AppEmbedBridge: FC<AppEmbedBridgeProps> = ({ children }) => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const [switchLocale, updateSystemStatus, setEmbedParams, setWorkspaceContext, isStatusInit] =
    useGlobalStore(s => [
      s.switchLocale,
      s.updateSystemStatus,
      s.setEmbedParams,
      s.setWorkspaceContext,
      s.isStatusInit,
      s.embedParams,
    ]);

  const embedData: EmbedParams = useMemo(() => {
    const embedDataFromSessionStorage = readEmbedStateFromSessionStorage();
    if (embedDataFromSessionStorage && embedDataFromSessionStorage.mode === 'iframe') {
      const embedDataFromParams = parseEmbedParams(searchParams);
      // BUGFIX: URL params must override cached embed workspace to avoid stale workspace_id
      // from sessionStorage causing workspace ping-pong with parent context.
      const mergedFromSession: EmbedParams = {
        ...embedDataFromSessionStorage,
        ...(embedDataFromParams.workspace_id && { workspace_id: embedDataFromParams.workspace_id }),
        ...(embedDataFromParams.organization_id && {
          organization_id: embedDataFromParams.organization_id,
        }),
        ...(embedDataFromParams.app_id && { app_id: embedDataFromParams.app_id }),
      };
      saveEmbedStateToSessionStorage(mergedFromSession);
      return mergedFromSession;
    }

    const embedDataFromParams = parseEmbedParams(searchParams);
    // Merge with store state as fallback for values not in URL
    const merged: EmbedParams = {
      debug: embedDataFromParams.debug,
      source: embedDataFromParams.source ?? null,
      workspace_id: embedDataFromParams.workspace_id ?? null,
      organization_id: embedDataFromParams.organization_id ?? null,
      mode: embedDataFromParams.mode ?? null,
      app_id: embedDataFromParams.app_id || 0,
    };
    saveEmbedStateToSessionStorage(merged);
    return merged;
  }, [searchParams]);
  const [workspaces, workspacesLoaded, selectedWorkspace, setSelectedWorkspace] = useWorkspaceStore(
    useShallow(s => [s.workspaces, s.workspacesLoaded, s.selectedWorkspace, s.setSelectedWorkspace])
  );
  const [isLoaded, logout] = useUserStore(s => [s.isLoaded, s.logout]);
  const screensLoaded = useUserStore(authSelectors.screensLoaded);
  const hasHandledInitialWorkspaceChange = useRef(false);
  const hasSentBridgeReady = useRef(false);
  const isEmbedIframe = embedData.mode === 'iframe';

  useEffect(() => {
    if (!isEmbedIframe) {
      updateSystemStatus({ isEmbedIframe: false });
      return;
    }

    updateSystemStatus({
      isEmbedIframe: true,
      showHeader: false,
      showGlobalLoading: false,
      showSidebarToggle: true,
      sideBarStyle: { backgroundColor: '#F4F4F4' },
      messageListStyle: { backgroundColor: '#FFF' },
    });
  }, [isEmbedIframe, isStatusInit, updateSystemStatus]);

  useEffect(() => {
    if (!isEmbedIframe) return;
    setEmbedParams(embedData);
  }, [embedData, isEmbedIframe, setEmbedParams]);

  if (embedData.debug) {
    console.log('[AppEmbedBridge] SEARCH_PARAMS appId:', embedData.app_id);
  }

  const patchWorkspaceContext = (nextPatch: Partial<WSContext>) => {
    if (Object.keys(nextPatch).length === 0) return;

    const currentContext = useGlobalStore.getState().workspaceContext ?? {};
    setWorkspaceContext({ ...currentContext, ...nextPatch });
  };

  // ============================== Event handlers ===============================
  const handleLanguageChanged = (locale: string) => {
    if (embedData.debug) {
      console.log('[AppEmbedBridge] LANGUAGE_CHANGED:', locale);
    }

    patchWorkspaceContext({ locale });
    switchLocale(locale as LocaleMode);
  };

  const handleContextUpdate = (ctx: WSContext) => {
    if (embedData.debug) {
      console.log('[AppEmbedBridge] CONTEXT_UPDATED:', ctx);
    }
    const currentEmbedParams = useGlobalStore.getState().embedParams;
    if (currentEmbedParams) {
      setEmbedParams({
        ...currentEmbedParams,
        ...(ctx.workspace_id && { workspace_id: ctx.workspace_id }),
        ...(ctx.org_id && { organization_id: ctx.org_id }),
      });
    }
  };
  const handleSessionExpired = async () => {
    if (embedData.debug) {
      console.log('[AppEmbedBridge] SESSION_EXPIRED');
    }
    // implement logout
    await logout();
  };
  const handleWorkspaceChanged = (workspaceId: string, workspaceName?: string): boolean => {
    if (embedData.debug) {
      console.log('[AppEmbedBridge] WORKSPACE_CHANGED:', workspaceId, workspaceName);
    }
    if (!workspaceId) return false;

    const nextWorkspace = workspaces.find(workspace => workspace.workspace_id === workspaceId);
    // BUGFIX: if parent sends a workspace not accessible by this user, ignore it.
    // Otherwise WorkspaceUrlSync will fallback to first workspace and fight this update forever.
    if (workspacesLoaded && !nextWorkspace) {
      if (embedData.debug) {
        console.warn('[AppEmbedBridge] IGNORE_WORKSPACE_CHANGE_NOT_ACCESSIBLE:', workspaceId);
      }
      // In iframe mode, inaccessible workspace from parent is an authorization mismatch.
      // Do not fallback to another workspace.
      if (isEmbedIframe) {
        router.replace('/403');
      }
      return false;
    }

    if (nextWorkspace && selectedWorkspace?.workspace_id !== workspaceId) {
      setSelectedWorkspace(nextWorkspace);
    }

    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set('workspace_id', workspaceId);
    nextUrl.searchParams.delete('workspace');

    const currentUrl = window.location.pathname + window.location.search;
    const nextRelativeUrl = nextUrl.pathname + nextUrl.search;

    if (nextRelativeUrl !== currentUrl) {
      router.replace(nextRelativeUrl, { scroll: false });
    }

    const currentEmbedParams = useGlobalStore.getState().embedParams;
    if (currentEmbedParams) {
      setEmbedParams({ ...currentEmbedParams, workspace_id: workspaceId });
    }

    patchWorkspaceContext({ workspace_id: workspaceId, workspace_name: workspaceName });
    return true;
  };

  // ============================== Bridge ===============================

  const { sendReady, sendHello, context } = useAppBridge({
    appId: embedData.app_id,
    debug: embedData.debug,
    onContextUpdate: handleContextUpdate,
    onLanguageChanged: handleLanguageChanged,
    onSessionExpired: handleSessionExpired,
    onWorkspaceChanged: handleWorkspaceChanged,
  });

  useEffect(() => {
    if (!context) return;

    setWorkspaceContext(context);

    // BUGFIX: defer the first context->workspace sync until workspace list is loaded,
    // so we can validate accessibility and avoid early race loops on initial mount.
    if (hasHandledInitialWorkspaceChange.current) return;
    if (!workspacesLoaded) return;
    if (!context.workspace_id) return;

    const handled = handleWorkspaceChanged(context.workspace_id, context.workspace_name);
    if (!handled) return;

    hasHandledInitialWorkspaceChange.current = true;
    if (embedData.debug) {
      console.log('[AppEmbedBridge] CONTEXT_UPDATED_INITIAL:', context);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context, workspacesLoaded, embedData.debug, setWorkspaceContext]);

  useEffect(() => {
    if (embedData.debug) {
      console.log('[AppEmbedBridge] IS_LOADED:', {
        isLoaded,
        screensLoaded,
        hasSentBridgeReady: hasSentBridgeReady.current,
      });
    }
    if (hasSentBridgeReady.current) return;
    if (!isLoaded) return;
    hasSentBridgeReady.current = true;
    sendReady('/', { debug: embedData.debug });
    if (embedData.debug) {
      console.log('[AppEmbedBridge] SEND_READY');
    }
    sendHello();
    if (embedData.debug) {
      console.log('[AppEmbedBridge] SEND_HELLO');
    }
  }, [embedData.debug, isLoaded, screensLoaded, sendHello, sendReady]);

  return <>{children}</>;
};

export default AppEmbedBridge;
